import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { allProducts } from '../../mocks/products';
import { Link } from 'react-router-dom';

const ProductsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');

  const categories = ['All', 'Dresses', 'Abayas', 'Hijabs', 'Pins & Brooches', 'Hair Accessories', 'Prayer Items'];

  const filteredProducts = selectedCategory === 'All' 
    ? allProducts 
    : allProducts.filter(product => product.category === selectedCategory);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    if (sortBy === 'rating') return b.rating - a.rating;
    return 0;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="relative bg-emerald-900 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d5727740837e5ea78fa79415201dacf3.jpg"
            alt="Our Collection"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-emerald-900/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-0.5 bg-amber-500 mr-4"></div>
            <span className="text-amber-400 text-sm uppercase tracking-widest font-medium">Explore</span>
            <div className="w-12 h-0.5 bg-amber-500 ml-4"></div>
          </div>
          <h1
            className="text-5xl md:text-6xl text-amber-50 mb-4 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Our Collection
          </h1>
          <p className="text-lg text-amber-200 max-w-2xl mx-auto">
            Discover our curated selection of premium modest wear and handcrafted accessories
          </p>
        </div>
      </section>

      {/* Filters and Products */}
      <section className="py-12 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Filter Bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-emerald-900 text-amber-100'
                      : 'bg-white text-emerald-900 border border-emerald-200 hover:border-amber-500 hover:text-amber-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-3">
              <span className="text-emerald-900 font-medium text-sm">Sort by:</span>
              <button
                onClick={() => {
                  const options = ['featured', 'price-low', 'price-high', 'rating'];
                  const currentIndex = options.indexOf(sortBy);
                  const nextIndex = (currentIndex + 1) % options.length;
                  setSortBy(options[nextIndex]);
                }}
                className="px-5 py-2 bg-white border border-emerald-200 rounded-full text-sm font-medium text-emerald-900 hover:border-amber-500 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {sortBy === 'featured' && 'Featured'}
                {sortBy === 'price-low' && 'Price: Low to High'}
                {sortBy === 'price-high' && 'Price: High to Low'}
                {sortBy === 'rating' && 'Highest Rated'}
                <i className="ri-arrow-down-s-line"></i>
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
            {filteredProducts.map((product) => (
              <Link key={product.id} to={`/product/${product.id}`}>
                <div className="group cursor-pointer">
                  <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <div className="relative">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-72 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 right-3">
                        <button className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-amber-50 transition-colors cursor-pointer">
                          <i className="ri-heart-line text-gray-600 text-sm"></i>
                        </button>
                      </div>
                      {product.originalPrice > product.price && (
                        <div className="absolute top-3 left-3 bg-amber-500 text-amber-950 px-3 py-1 rounded-full text-xs font-medium">
                          Sale
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                        <span className="px-5 py-2 bg-amber-500 text-amber-950 rounded-full text-sm font-medium whitespace-nowrap">
                          Quick View
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-emerald-900 mb-2 line-clamp-2">{product.name}</h3>
                      <div className="flex items-center mb-3">
                        <div className="flex text-amber-500 text-xs">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'}`}></i>
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 ml-1">({product.reviews})</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-amber-600">${product.price}</span>
                          {product.originalPrice > product.price && (
                            <span className="text-xs text-gray-400 line-through">${product.originalPrice}</span>
                          )}
                        </div>
                        <span className="px-3 py-1 bg-emerald-900 text-amber-100 rounded-full text-xs font-medium whitespace-nowrap">
                          View
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* No Results */}
          {sortedProducts.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-shopping-bag-line text-6xl text-amber-300 mb-4"></i>
              <h3 className="text-2xl font-semibold text-emerald-900 mb-2">No products found</h3>
              <p className="text-gray-600">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
